import { showError } from '@calendly/alert'
import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { nanoid } from 'nanoid'
import { useDispatch } from 'react-redux'

import { useETEditorFormikPoolingType } from 'services/event_types'

import { findUserPool, getDefaultUserPoolName, getNextUserPoolName, updateMemberships } from '../helpers'
import { MAX_ROUND_ROBIN_USER_POOLS } from '../utils'
import { useUserPoolsData } from './use_user_pools_data'
import { useAddMissingEventTypeMemberships } from '../../availability/utils'

export const useAddRoundRobinHosts = (eventType) => {
  const isNewEventType = !eventType?.id
  const dispatch = useDispatch()
  const { values: { memberships, allocationPreference }, setValues } = useFormContext()
  const { userPools, roundRobinUserPools } = useUserPoolsData()
  const { isCollective, isRoundRobin, isCollectiveRoundRobin } = useETEditorFormikPoolingType()
  const { isTeam } = eventType
  const addMissingEventTypeMemberships = useAddMissingEventTypeMemberships()

  return (eventTypeMemberships, userPoolUid) => {
    // during creation when we add a member we need to add availability rules for newly added
    // no need to do it during edit since those will come from the back-end after section save
    const eventTypeMembershipsAttributes = isNewEventType ? {
      event_type_memberships_attributes: addMissingEventTypeMemberships(eventTypeMemberships)
    } : {}

    if (isRoundRobin) {
      // When Round Robin
      // Then we update existing memberships
      return setValues((values) => ({
        ...values,
        memberships: updateMemberships(memberships, eventTypeMemberships, {
          addPriority: true,
          markForDestruction: !isTeam
        }),
        ...eventTypeMembershipsAttributes
      }))
    }

    if (isCollective) {
      // When Collective
      // Then we convert it to Collective Round Robin
      const roundRobinPoolKey = nanoid(5)
      const collectivePoolKey = nanoid(5)

      const updatedCollectivePoolMemberships = updateMemberships([], memberships, {
        userPoolUid: collectivePoolKey,
        markForDestruction: !isTeam
      })

      const updatedMemberships = updateMemberships(updatedCollectivePoolMemberships, eventTypeMemberships, {
        userPoolUid: roundRobinPoolKey,
        addPriority: true,
        markForDestruction: !isTeam
      })

      const hasNoNewMemberships = !updatedMemberships.some((membership) => membership.user_pool_uid === roundRobinPoolKey)
      if (hasNoNewMemberships) { return null }

      return setValues((values) => ({
        ...values,
        allocationPreference: null,
        pooling_type: 'multi_pool',
        userPools: [
          {
            key: collectivePoolKey,
            allocation_preference: allocationPreference || 'availability',
            pooling_type: 'collective',
            position: 0
          },
          {
            key: roundRobinPoolKey,
            allocation_preference: 'availability',
            pooling_type: 'round_robin',
            name: getDefaultUserPoolName(),
            position: 1
          }
        ],
        memberships: updatedMemberships,
        ...eventTypeMembershipsAttributes
      }))
    }

    if (!isCollectiveRoundRobin) return null

    const roundRobinUserPool = findUserPool(roundRobinUserPools, userPoolUid)

    // When Collective Round Robin has a round-robin user pool,
    // Then we update memberships in it
    if (roundRobinUserPool) {
      return setValues((values) => ({
        ...values,
        memberships: updateMemberships(memberships, eventTypeMemberships, {
          userPoolUid,
          addPriority: true,
          markForDestruction: !isTeam
        }),
        ...eventTypeMembershipsAttributes
      }))
    }

    if (roundRobinUserPools.length === MAX_ROUND_ROBIN_USER_POOLS) {
      // We do not allow creating more than MAX_ROUND_ROBIN_USER_POOLS UserPools
      return dispatch(showError(I18n.t('publisher.event_types.members.validation.max_pools_reached')))
    }

    // When Collective Round Robin does not have a round-robin user pool,
    // Then we add a round-robin user pool with hosts
    const roundRobinPoolKey = nanoid(5)

    const updatedMemberships = updateMemberships(memberships, eventTypeMemberships, {
      userPoolUid: roundRobinPoolKey,
      addPriority: true,
      markForDestruction: !isTeam
    })

    const hasNoNewMemberships = !updatedMemberships.some((membership) => membership.user_pool_uid === roundRobinPoolKey)
    if (hasNoNewMemberships) { return null }

    return setValues((values) => ({
      ...values,
      userPools: [
        ...userPools,
        {
          key: roundRobinPoolKey,
          allocation_preference: 'availability',
          pooling_type: 'round_robin',
          name: getNextUserPoolName(roundRobinUserPools),
          position: userPools.length
        }
      ],
      memberships: updatedMemberships,
      ...eventTypeMembershipsAttributes
    }))
  }
}
